import { createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { RootState } from '../..';
import {
  createHomeworkSubmissionAsAdmin,
  deleteHomeworkSubmissionAsAdmin,
  getHomeworkSubmissionsAsAdmin,
  updateHomeworkSubmissionAsAdmin,
} from './adminApi';
import { IUser } from '../../UserSlice';
import {
  createHomeworkSubmission,
  deleteHomeworkSubmission,
  getHomeworkSubmissions,
  getHomeworkSubmissionsByCourse,
  updateHomeworkSubmission,
} from './api';
import { ISection } from '../SectionSlice';

export type THomeworkFile = {
  name: string;
  url: string;
};

export type THomeworkQuiz = {
  _id: string;
  topic: string;
  difficulty: string;
  noOfQuestions: number;
  subTopic: string;
  link: string;
};

export interface IHomework {
  _id?: string;
  section: string | ISection;
  description: string;
  createdBy: IUser;
  student?: string;
  files?: Array<THomeworkFile>;
  link?: string;
  quizzes?: Array<THomeworkQuiz>;
  createdAt: string;
}

interface IHomeworkExtended extends IHomework {
  score?: number;
  totalScore?: number;
  updatedAt?: string;
  timerStart?: number;
  timerEnd?: number;
  completedAt?: number;
  attempt?: number;
  user?: string;
}

interface ITrialsState {
  status: 'idle' | 'loading' | 'rejected';
  homeworkSubmissions?: Array<IHomeworkExtended>;
  homeworkSubmissionsByCourse?: Array<IHomeworkExtended>;
}

const initialState: ITrialsState = {
  status: 'idle',
  homeworkSubmissions: undefined,
  homeworkSubmissionsByCourse: undefined,
};

export const HomeworksSlice = createSlice({
  name: 'homeworks',
  initialState,
  reducers: {
    resetHomeworkSubmissions: (state) => {
      state.homeworkSubmissions = [];
    },
    resetHomeworkSubmissionsByCourse: (state) => {
      state.homeworkSubmissionsByCourse = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHomeworkSubmissions.fulfilled, (state, action) => {
        state.status = 'idle';
        state.homeworkSubmissions = action.payload;
      })
      .addCase(getHomeworkSubmissionsByCourse.fulfilled, (state, action) => {
        state.status = 'idle';
        state.homeworkSubmissionsByCourse = action.payload;
      })
      .addCase(createHomeworkSubmission.fulfilled, (state, action) => {
        state.status = 'idle';
        state.homeworkSubmissions = [
          ...(state.homeworkSubmissions || []),
          action.payload,
        ];
      })
      .addCase(updateHomeworkSubmission.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          action.payload &&
          state.homeworkSubmissions &&
          state.homeworkSubmissions.length
        ) {
          const index = state.homeworkSubmissions.findIndex(
            (item) => item._id === action.payload._id
          );

          if (index !== -1) {
            state.homeworkSubmissions[index] = action.payload;
          }
        }
      })
      .addCase(deleteHomeworkSubmission.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          action.payload &&
          state.homeworkSubmissions &&
          state.homeworkSubmissions.length
        ) {
          const newHomeworkSubmissions = state.homeworkSubmissions.filter(
            (newFile) => action.payload._id !== newFile._id
          );

          state.homeworkSubmissions = newHomeworkSubmissions;
        }
      })

      // Admin
      .addCase(getHomeworkSubmissionsAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.homeworkSubmissions = action.payload;
      })
      .addCase(createHomeworkSubmissionAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';
        state.homeworkSubmissions = [
          ...(state.homeworkSubmissions || []),
          action.payload,
        ];
      })
      .addCase(updateHomeworkSubmissionAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          action.payload &&
          state.homeworkSubmissions &&
          state.homeworkSubmissions.length
        ) {
          const index = state.homeworkSubmissions.findIndex(
            (item) => item._id === action.payload._id
          );

          if (index !== -1) {
            state.homeworkSubmissions[index] = action.payload;
          }
        }
      })
      .addCase(deleteHomeworkSubmissionAsAdmin.fulfilled, (state, action) => {
        state.status = 'idle';

        if (
          action.payload &&
          state.homeworkSubmissions &&
          state.homeworkSubmissions.length
        ) {
          const newHomeworkSubmissions = state.homeworkSubmissions.filter(
            (newFile) => action.payload._id !== newFile._id
          );

          state.homeworkSubmissions = newHomeworkSubmissions;
        }
      });

    // Matchers
    builder
      .addMatcher(
        isPending(
          getHomeworkSubmissions,
          createHomeworkSubmission,
          updateHomeworkSubmission,
          deleteHomeworkSubmission,
          getHomeworkSubmissionsAsAdmin,
          createHomeworkSubmissionAsAdmin,
          updateHomeworkSubmissionAsAdmin,
          deleteHomeworkSubmissionAsAdmin
        ),
        (state) => {
          state.status = 'loading';
        }
      )
      .addMatcher(
        isRejected(
          getHomeworkSubmissions,
          createHomeworkSubmission,
          updateHomeworkSubmission,
          deleteHomeworkSubmission,
          getHomeworkSubmissionsAsAdmin,
          createHomeworkSubmissionAsAdmin,
          updateHomeworkSubmissionAsAdmin,
          deleteHomeworkSubmissionAsAdmin
        ),
        (state) => {
          state.status = 'rejected';
        }
      );
  },
});

export const {
  resetHomeworkSubmissions,
  resetHomeworkSubmissionsByCourse,
} = HomeworksSlice.actions;

export const getHomeworksState = (state: RootState): RootState['homeworks'] =>
  state.homeworks;

export default HomeworksSlice.reducer;
